/**
 * Data governance admin page ('gov.*'): governed datasets, row/column
 * policies, user attributes, "test as user" and the access log, plus the
 * chat status line shown while an answer is checked. The Arabic table must
 * cover every key.
 */
const en = {
  'gov.title': 'Data Governance',
  'gov.subtitle': 'Decide which rows and columns of company data each person can reach through AI agents. Rules are enforced on every query and checked on every answer.',
  'gov.sections': 'Data governance sections',
  'gov.loadFailed': 'Could not load data governance settings.',
  'gov.duplicate': 'Duplicate',

  'gov.tab.policies': 'Policies',
  'gov.tab.datasets': 'Datasets',
  'gov.tab.attributes': 'User attributes',
  'gov.tab.test': 'Test as user',
  'gov.tab.activity': 'Activity',

  'gov.summary': 'Data governance summary',
  'gov.stat.datasets': 'Governed datasets',
  'gov.stat.policies': 'Active policies',
  'gov.stat.attributes': 'Users with attributes',
  'gov.stat.blocked': 'Refused or redacted',
  'gov.stat.blockedHint': 'in recent activity',
  'gov.stat.total': '{n} in total',

  // Policies
  'gov.policies.heading': 'Policies',
  'gov.policies.desc': 'A policy says who may see which rows of a dataset, and which columns stay hidden or masked. Anyone no policy applies to sees nothing of that dataset.',
  'gov.policies.add': '+ New policy',
  'gov.policies.filter': 'Dataset',
  'gov.policies.allDatasets': 'All datasets',
  'gov.policies.empty': 'No policies yet',
  'gov.policies.emptyHint': 'Create a policy to give people access to a dataset.',
  'gov.policies.needDataset': 'Add a dataset first, then create policies for it.',
  'gov.policies.appliesTo': 'Applies to',
  'gov.policies.rows': 'Rows',
  'gov.policies.hidden': 'Hidden',
  'gov.policies.masked': 'Masked',
  'gov.policies.problem': "This policy can't be applied: {problem}",
  'gov.policies.editName': 'Edit {name}',
  'gov.policies.deleteName': 'Delete {name}',
  'gov.policies.duplicateName': 'Duplicate {name}',
  'gov.policies.deleteTitle': 'Delete policy "{name}"?',
  'gov.policies.deleteMessage': 'The policy is removed from its dataset.',
  'gov.policies.deleteConsequence': 'People who only had access through this policy lose it immediately.',
  'gov.policies.deleted': 'Policy deleted',
  'gov.policies.saved': 'Policy saved',
  'gov.policies.saveFailed': 'Could not save the policy.',
  'gov.policies.deleteFailed': 'Could not delete the policy.',
  'gov.policies.copyOf': 'Copy of {name}',

  'gov.subject.everyone': 'Everyone',
  'gov.subject.user': 'User',
  'gov.subject.department': 'Department',
  'gov.subject.role': 'Role',
  'gov.subject.attribute': 'Attribute',
  'gov.subject.departmentX': '{name} (department)',
  'gov.subject.roleX': 'Role: {name}',
  'gov.subject.attributeX': 'Everyone with {key} = {value}',
  'gov.subject.unknownUser': 'User #{id}',
  'gov.subject.unknownDept': 'Department #{id}',

  // Policy editor
  'gov.editor.createTitle': 'New policy',
  'gov.editor.editTitle': 'Edit policy',
  'gov.editor.name': 'Policy name',
  'gov.editor.namePlaceholder': 'e.g., Payroll: own cost center, under 5,000',
  'gov.editor.description': 'Description',
  'gov.editor.descriptionPlaceholder': 'Why this policy exists (optional)',
  'gov.editor.dataset': 'Dataset',
  'gov.editor.chooseDataset': 'Choose a dataset',
  'gov.editor.who': 'Who it applies to',
  'gov.editor.whoHint': 'Add people, departments, roles, or everyone with a given attribute.',
  'gov.editor.subjectType': 'Type',
  'gov.editor.addSubject': 'Add',
  'gov.editor.removeSubject': 'Remove {name}',
  'gov.editor.noSubjects': 'Nobody yet. Add at least one.',
  'gov.editor.chooseUser': 'Choose a user',
  'gov.editor.chooseDepartment': 'Choose a department',
  'gov.editor.attrKey': 'Attribute',
  'gov.editor.attrValue': 'Value',
  'gov.editor.rows': 'Rows they can see',
  'gov.editor.match': 'Match',
  'gov.editor.match.all': 'All conditions',
  'gov.editor.match.any': 'Any condition',
  'gov.editor.noConditions': 'No conditions: they see every row of the dataset.',
  'gov.editor.addCondition': '+ Add condition',
  'gov.editor.removeCondition': 'Remove condition {n}',
  'gov.editor.column': 'Column',
  'gov.editor.operator': 'Condition',
  'gov.editor.value': 'Value',
  'gov.editor.valueFrom': 'Compare with',
  'gov.editor.valueMode.fixed': 'A fixed value',
  'gov.editor.valueMode.attribute': "The user's attribute",
  'gov.editor.chooseAttribute': 'Choose an attribute',
  'gov.editor.listHint': 'Separate values with commas',
  'gov.editor.and': 'and',
  'gov.editor.columns': 'Columns they can see',
  'gov.editor.columnsHint': 'Restricted columns stay hidden unless you choose otherwise here.',
  'gov.editor.defaultAction': 'Columns without their own rule',
  'gov.editor.useDefault': 'Default: {action}',
  'gov.editor.restrictedDefault': 'Default: hide (restricted)',
  'gov.editor.summary': 'In plain words',
  'gov.editor.create': 'Create policy',
  'gov.editor.save': 'Save policy',
  'gov.editor.nameRequired': 'Give the policy a name.',
  'gov.editor.datasetRequired': 'Choose a dataset.',
  'gov.editor.subjectRequired': 'Add at least one person, department, role or attribute.',

  'gov.op.eq': 'equals',
  'gov.op.neq': 'does not equal',
  'gov.op.lt': 'is less than',
  'gov.op.lte': 'is at most',
  'gov.op.gt': 'is greater than',
  'gov.op.gte': 'is at least',
  'gov.op.in': 'is one of',
  'gov.op.not_in': 'is not one of',
  'gov.op.between': 'is between',
  'gov.op.contains': 'contains',
  'gov.op.starts_with': 'starts with',
  'gov.op.is_null': 'is empty',
  'gov.op.not_null': 'is not empty',

  'gov.action.show': 'Show',
  'gov.action.partial': 'Show last 4 only',
  'gov.action.mask': 'Mask (****)',
  'gov.action.hide': 'Hide',
  'gov.action.varies': 'Depends on the row',

  'gov.type.string': 'Text',
  'gov.type.number': 'Number',
  'gov.type.date': 'Date',
  'gov.type.timestamp': 'Date and time',
  'gov.type.boolean': 'Yes / No',

  'gov.sens.public': 'Public',
  'gov.sens.internal': 'Internal',
  'gov.sens.confidential': 'Confidential',
  'gov.sens.restricted': 'Restricted',

  'gov.sum.appliesTo': 'Applies to {who}.',
  'gov.sum.nobody': 'nobody yet',
  'gov.sum.rowsAll': 'They can see every row of {dataset}.',
  'gov.sum.rows': 'They can see {dataset} rows where {rule}.',
  'gov.sum.hidden': 'Hidden: {cols}.',
  'gov.sum.masked': 'Masked: {cols}.',
  'gov.sum.theirAttr': 'their {key}',
  'gov.sum.and': 'and',
  'gov.sum.or': 'or',

  // Datasets
  'gov.ds.heading': 'Datasets',
  'gov.ds.desc': 'A dataset is an Athena table agents may read. Agents query it by its query name, and the Policy Guard swaps in a filtered, masked view for each person.',
  'gov.ds.add': '+ New dataset',
  'gov.ds.empty': 'No governed datasets yet',
  'gov.ds.emptyHint': 'Add the Athena tables your agents use, such as payroll or budgets.',
  'gov.ds.table': 'Athena table',
  'gov.ds.key': 'Query name',
  'gov.ds.columnsCount': '{n} columns',
  'gov.ds.policiesCount': '{n} policies',
  'gov.ds.allAgents': 'All agents',
  'gov.ds.agentsCount': '{n} agents',
  'gov.ds.problem': "This dataset can't be used: {problem}",
  'gov.ds.editName': 'Edit {name}',
  'gov.ds.deleteName': 'Delete {name}',
  'gov.ds.deleteTitle': 'Delete dataset "{name}"?',
  'gov.ds.deleteMessage': 'Its {n} policies are deleted with it.',
  'gov.ds.deleteConsequence': 'Agents can no longer query this data through the Policy Guard.',
  'gov.ds.deleted': 'Dataset deleted',
  'gov.ds.saved': 'Dataset saved',
  'gov.ds.saveFailed': 'Could not save the dataset.',
  'gov.ds.deleteFailed': 'Could not delete the dataset.',

  'gov.dse.createTitle': 'New dataset',
  'gov.dse.editTitle': 'Edit dataset',
  'gov.dse.name': 'Name',
  'gov.dse.namePlaceholder': 'e.g., Payroll',
  'gov.dse.key': 'Query name',
  'gov.dse.keyHint': 'Agents use this as the table name, e.g. SELECT … FROM payroll. Lowercase letters, digits and underscores.',
  'gov.dse.description': 'Description',
  'gov.dse.descriptionPlaceholder': 'What the data is, so agents know when to use it',
  'gov.dse.database': 'Athena database',
  'gov.dse.tableName': 'Athena table',
  'gov.dse.loadColumns': 'Load columns from Athena',
  'gov.dse.loading': 'Loading…',
  'gov.dse.loaded': '{n} columns loaded',
  'gov.dse.loadFailed': 'Could not read the table.',
  'gov.dse.columns': 'Columns',
  'gov.dse.columnsHint': 'Only these columns are ever exposed. Mark sensitive ones: restricted columns are hidden unless a policy shows them.',
  'gov.dse.colName': 'Column',
  'gov.dse.colLabel': 'Label',
  'gov.dse.colType': 'Type',
  'gov.dse.colSensitivity': 'Sensitivity',
  'gov.dse.addColumn': '+ Add column',
  'gov.dse.removeColumn': 'Remove column {name}',
  'gov.dse.noColumns': 'No columns yet. Load them from Athena or add them by hand.',
  'gov.dse.agents': 'Agents',
  'gov.dse.agentsAll': 'All agents',
  'gov.dse.agentsSome': 'Only selected agents',
  'gov.dse.create': 'Create dataset',
  'gov.dse.save': 'Save dataset',

  // Attributes
  'gov.attr.heading': 'User attributes',
  'gov.attr.desc': 'Facts about people that policies can use, such as cost center, employee number or grade. In a rule, compare a column with "the user\'s attribute".',
  'gov.attr.builtins': 'Always available from the profile: {list}.',
  'gov.attr.import': 'Import CSV',
  'gov.attr.template': 'Download template',
  'gov.attr.importHint': 'First column: email. Then one column per attribute. Blank cells leave values unchanged.',
  'gov.attr.importing': 'Importing…',
  'gov.attr.importDone': '{users} users updated, {values} values set.',
  'gov.attr.importUnknown': '{n} email(s) not found: {list}',
  'gov.attr.importFailed': 'Import failed.',
  'gov.attr.importTitle': 'Attributes imported',
  'gov.attr.search': 'Search users',
  'gov.attr.colUser': 'User',
  'gov.attr.colDept': 'Department',
  'gov.attr.colAttrs': 'Attributes',
  'gov.attr.none': 'None',
  'gov.attr.edit': 'Edit',
  'gov.attr.editFor': 'Edit attributes of {name}',
  'gov.attr.editTitle': 'Attributes: {name}',
  'gov.attr.key': 'Attribute',
  'gov.attr.value': 'Value',
  'gov.attr.add': '+ Add attribute',
  'gov.attr.remove': 'Remove {key}',
  'gov.attr.keyHint': 'Lowercase names, e.g. cost_center. Separate several values with commas (e.g. a manager with two cost centers).',
  'gov.attr.saved': 'Attributes saved',
  'gov.attr.saveFailed': 'Could not save the attributes.',
  'gov.attr.empty': 'No users match.',
  'gov.attr.invalidKey': '"{key}" is not a valid attribute name.',

  // Test as user
  'gov.test.heading': 'Test as user',
  'gov.test.desc': 'See exactly what a person would get, computed by the same engine that enforces it.',
  'gov.test.user': 'User',
  'gov.test.chooseUser': 'Choose a user',
  'gov.test.agent': 'Agent',
  'gov.test.anyAgent': 'All datasets (any agent)',
  'gov.test.check': 'Check access',
  'gov.test.checking': 'Checking…',
  'gov.test.allowed': 'Has access',
  'gov.test.denied': 'No access',
  'gov.test.rowScope': 'Rows: {scope}',
  'gov.test.columns': 'Columns',
  'gov.test.policies': 'Policies',
  'gov.test.policyApplied': 'Applied',
  'gov.test.policyNotApplies': 'Does not apply to this user',
  'gov.test.policySkipped': 'Applies, but skipped: {reason}',
  'gov.test.sql': 'Generated view (SQL)',
  'gov.test.attributes': 'Attributes: {list}',
  'gov.test.noAttributes': 'This user has no attributes.',
  'gov.test.noDatasets': 'No governed datasets for this agent.',
  'gov.test.query': 'Try a query',
  'gov.test.queryHint': 'Runs on Athena as this user, through the Policy Guard, and is logged with your name. Shows up to 50 rows.',
  'gov.test.run': 'Run query',
  'gov.test.running': 'Running…',
  'gov.test.rows': '{n} rows',
  'gov.test.truncated': 'More rows exist; only the first {n} are shown.',
  'gov.test.refused': 'Refused: {error}',
  'gov.test.failed': 'Could not run the check.',

  // Activity
  'gov.log.heading': 'Activity',
  'gov.log.desc': 'Every governed query, refusal and redaction. Returned data and leaked values are never stored.',
  'gov.log.filter': 'Show',
  'gov.log.all': 'Everything',
  'gov.log.action.query': 'Query',
  'gov.log.action.denied': 'Refused',
  'gov.log.action.redacted': 'Redacted',
  'gov.log.action.withheld': 'Withheld',
  'gov.log.refresh': 'Refresh',
  'gov.log.colTime': 'Time',
  'gov.log.colUser': 'User',
  'gov.log.colAgent': 'Agent',
  'gov.log.colAction': 'Event',
  'gov.log.colDatasets': 'Datasets',
  'gov.log.colRows': 'Rows',
  'gov.log.colDetail': 'Detail',
  'gov.log.empty': 'Nothing recorded yet.',
  'gov.log.loadFailed': 'Could not load the activity log.',
};

const ar: Record<keyof typeof en, string> = {
  'gov.title': 'حوكمة البيانات',
  'gov.subtitle': 'حدّد الصفوف والأعمدة من بيانات الشركة التي يمكن لكل شخص الوصول إليها عبر وكلاء الذكاء الاصطناعي. تُطبَّق القواعد على كل استعلام وتُفحص كل إجابة.',
  'gov.sections': 'أقسام حوكمة البيانات',
  'gov.loadFailed': 'تعذّر تحميل إعدادات حوكمة البيانات.',
  'gov.duplicate': 'تكرار',

  'gov.tab.policies': 'السياسات',
  'gov.tab.datasets': 'مجموعات البيانات',
  'gov.tab.attributes': 'سمات المستخدمين',
  'gov.tab.test': 'اختبار كمستخدم',
  'gov.tab.activity': 'النشاط',

  'gov.summary': 'ملخص حوكمة البيانات',
  'gov.stat.datasets': 'مجموعات بيانات محكومة',
  'gov.stat.policies': 'سياسات نشطة',
  'gov.stat.attributes': 'مستخدمون لديهم سمات',
  'gov.stat.blocked': 'مرفوض أو محجوب',
  'gov.stat.blockedHint': 'في النشاط الأخير',
  'gov.stat.total': '{n} إجمالاً',

  'gov.policies.heading': 'السياسات',
  'gov.policies.desc': 'تحدد السياسة من يمكنه رؤية أي صفوف من مجموعة البيانات، وأي الأعمدة تبقى مخفية أو مقنّعة. من لا تنطبق عليه أي سياسة لا يرى شيئاً من مجموعة البيانات تلك.',
  'gov.policies.add': '+ سياسة جديدة',
  'gov.policies.filter': 'مجموعة البيانات',
  'gov.policies.allDatasets': 'كل مجموعات البيانات',
  'gov.policies.empty': 'لا توجد سياسات بعد',
  'gov.policies.emptyHint': 'أنشئ سياسة لمنح الأشخاص وصولاً إلى مجموعة بيانات.',
  'gov.policies.needDataset': 'أضف مجموعة بيانات أولاً، ثم أنشئ سياسات لها.',
  'gov.policies.appliesTo': 'تنطبق على',
  'gov.policies.rows': 'الصفوف',
  'gov.policies.hidden': 'مخفي',
  'gov.policies.masked': 'مقنّع',
  'gov.policies.problem': 'لا يمكن تطبيق هذه السياسة: {problem}',
  'gov.policies.editName': 'تعديل {name}',
  'gov.policies.deleteName': 'حذف {name}',
  'gov.policies.duplicateName': 'تكرار {name}',
  'gov.policies.deleteTitle': 'حذف السياسة "{name}"؟',
  'gov.policies.deleteMessage': 'ستُزال السياسة من مجموعة البيانات الخاصة بها.',
  'gov.policies.deleteConsequence': 'من كان وصوله عبر هذه السياسة فقط سيفقده فوراً.',
  'gov.policies.deleted': 'تم حذف السياسة',
  'gov.policies.saved': 'تم حفظ السياسة',
  'gov.policies.saveFailed': 'تعذّر حفظ السياسة.',
  'gov.policies.deleteFailed': 'تعذّر حذف السياسة.',
  'gov.policies.copyOf': 'نسخة من {name}',

  'gov.subject.everyone': 'الجميع',
  'gov.subject.user': 'مستخدم',
  'gov.subject.department': 'قسم',
  'gov.subject.role': 'دور',
  'gov.subject.attribute': 'سمة',
  'gov.subject.departmentX': '{name} (قسم)',
  'gov.subject.roleX': 'الدور: {name}',
  'gov.subject.attributeX': 'كل من لديه {key} = {value}',
  'gov.subject.unknownUser': 'مستخدم #{id}',
  'gov.subject.unknownDept': 'قسم #{id}',

  'gov.editor.createTitle': 'سياسة جديدة',
  'gov.editor.editTitle': 'تعديل السياسة',
  'gov.editor.name': 'اسم السياسة',
  'gov.editor.namePlaceholder': 'مثال: الرواتب: مركز التكلفة الخاص، أقل من 5,000',
  'gov.editor.description': 'الوصف',
  'gov.editor.descriptionPlaceholder': 'سبب وجود هذه السياسة (اختياري)',
  'gov.editor.dataset': 'مجموعة البيانات',
  'gov.editor.chooseDataset': 'اختر مجموعة بيانات',
  'gov.editor.who': 'على من تنطبق',
  'gov.editor.whoHint': 'أضف أشخاصاً أو أقساماً أو أدواراً، أو كل من لديه سمة معيّنة.',
  'gov.editor.subjectType': 'النوع',
  'gov.editor.addSubject': 'إضافة',
  'gov.editor.removeSubject': 'إزالة {name}',
  'gov.editor.noSubjects': 'لا أحد بعد. أضف واحداً على الأقل.',
  'gov.editor.chooseUser': 'اختر مستخدماً',
  'gov.editor.chooseDepartment': 'اختر قسماً',
  'gov.editor.attrKey': 'السمة',
  'gov.editor.attrValue': 'القيمة',
  'gov.editor.rows': 'الصفوف التي يمكنهم رؤيتها',
  'gov.editor.match': 'المطابقة',
  'gov.editor.match.all': 'كل الشروط',
  'gov.editor.match.any': 'أي شرط',
  'gov.editor.noConditions': 'لا توجد شروط: يرون كل صفوف مجموعة البيانات.',
  'gov.editor.addCondition': '+ إضافة شرط',
  'gov.editor.removeCondition': 'إزالة الشرط {n}',
  'gov.editor.column': 'العمود',
  'gov.editor.operator': 'الشرط',
  'gov.editor.value': 'القيمة',
  'gov.editor.valueFrom': 'قارن مع',
  'gov.editor.valueMode.fixed': 'قيمة ثابتة',
  'gov.editor.valueMode.attribute': 'سمة المستخدم',
  'gov.editor.chooseAttribute': 'اختر سمة',
  'gov.editor.listHint': 'افصل القيم بفواصل',
  'gov.editor.and': 'و',
  'gov.editor.columns': 'الأعمدة التي يمكنهم رؤيتها',
  'gov.editor.columnsHint': 'تبقى الأعمدة المقيّدة مخفية ما لم تختر غير ذلك هنا.',
  'gov.editor.defaultAction': 'الأعمدة التي ليس لها قاعدة خاصة',
  'gov.editor.useDefault': 'الافتراضي: {action}',
  'gov.editor.restrictedDefault': 'الافتراضي: إخفاء (مقيّد)',
  'gov.editor.summary': 'بكلمات بسيطة',
  'gov.editor.create': 'إنشاء السياسة',
  'gov.editor.save': 'حفظ السياسة',
  'gov.editor.nameRequired': 'أدخل اسماً للسياسة.',
  'gov.editor.datasetRequired': 'اختر مجموعة بيانات.',
  'gov.editor.subjectRequired': 'أضف شخصاً أو قسماً أو دوراً أو سمة واحدة على الأقل.',

  'gov.op.eq': 'يساوي',
  'gov.op.neq': 'لا يساوي',
  'gov.op.lt': 'أقل من',
  'gov.op.lte': 'لا يزيد عن',
  'gov.op.gt': 'أكبر من',
  'gov.op.gte': 'لا يقل عن',
  'gov.op.in': 'واحد من',
  'gov.op.not_in': 'ليس واحداً من',
  'gov.op.between': 'بين',
  'gov.op.contains': 'يحتوي على',
  'gov.op.starts_with': 'يبدأ بـ',
  'gov.op.is_null': 'فارغ',
  'gov.op.not_null': 'غير فارغ',

  'gov.action.show': 'إظهار',
  'gov.action.partial': 'إظهار آخر 4 فقط',
  'gov.action.mask': 'تقنيع (****)',
  'gov.action.hide': 'إخفاء',
  'gov.action.varies': 'حسب الصف',

  'gov.type.string': 'نص',
  'gov.type.number': 'رقم',
  'gov.type.date': 'تاريخ',
  'gov.type.timestamp': 'تاريخ ووقت',
  'gov.type.boolean': 'نعم / لا',

  'gov.sens.public': 'عام',
  'gov.sens.internal': 'داخلي',
  'gov.sens.confidential': 'سري',
  'gov.sens.restricted': 'مقيّد',

  'gov.sum.appliesTo': 'تنطبق على {who}.',
  'gov.sum.nobody': 'لا أحد بعد',
  'gov.sum.rowsAll': 'يمكنهم رؤية كل صفوف {dataset}.',
  'gov.sum.rows': 'يمكنهم رؤية صفوف {dataset} حيث {rule}.',
  'gov.sum.hidden': 'مخفي: {cols}.',
  'gov.sum.masked': 'مقنّع: {cols}.',
  'gov.sum.theirAttr': '{key} الخاص بهم',
  'gov.sum.and': 'و',
  'gov.sum.or': 'أو',

  'gov.ds.heading': 'مجموعات البيانات',
  'gov.ds.desc': 'مجموعة البيانات هي جدول في Athena يمكن للوكلاء قراءته. يستعلم الوكلاء عنها باسم الاستعلام، ويستبدلها حارس السياسات بعرض مُصفّى ومقنّع لكل شخص.',
  'gov.ds.add': '+ مجموعة بيانات جديدة',
  'gov.ds.empty': 'لا توجد مجموعات بيانات محكومة بعد',
  'gov.ds.emptyHint': 'أضف جداول Athena التي يستخدمها وكلاؤك، مثل الرواتب أو الميزانيات.',
  'gov.ds.table': 'جدول Athena',
  'gov.ds.key': 'اسم الاستعلام',
  'gov.ds.columnsCount': '{n} أعمدة',
  'gov.ds.policiesCount': '{n} سياسات',
  'gov.ds.allAgents': 'كل الوكلاء',
  'gov.ds.agentsCount': '{n} وكلاء',
  'gov.ds.problem': 'لا يمكن استخدام مجموعة البيانات هذه: {problem}',
  'gov.ds.editName': 'تعديل {name}',
  'gov.ds.deleteName': 'حذف {name}',
  'gov.ds.deleteTitle': 'حذف مجموعة البيانات "{name}"؟',
  'gov.ds.deleteMessage': 'ستُحذف سياساتها ({n}) معها.',
  'gov.ds.deleteConsequence': 'لن يتمكن الوكلاء بعد الآن من الاستعلام عن هذه البيانات عبر حارس السياسات.',
  'gov.ds.deleted': 'تم حذف مجموعة البيانات',
  'gov.ds.saved': 'تم حفظ مجموعة البيانات',
  'gov.ds.saveFailed': 'تعذّر حفظ مجموعة البيانات.',
  'gov.ds.deleteFailed': 'تعذّر حذف مجموعة البيانات.',

  'gov.dse.createTitle': 'مجموعة بيانات جديدة',
  'gov.dse.editTitle': 'تعديل مجموعة البيانات',
  'gov.dse.name': 'الاسم',
  'gov.dse.namePlaceholder': 'مثال: الرواتب',
  'gov.dse.key': 'اسم الاستعلام',
  'gov.dse.keyHint': 'يستخدمه الوكلاء كاسم للجدول، مثل SELECT … FROM payroll. أحرف إنجليزية صغيرة وأرقام وشرطات سفلية.',
  'gov.dse.description': 'الوصف',
  'gov.dse.descriptionPlaceholder': 'ما هي هذه البيانات، ليعرف الوكلاء متى يستخدمونها',
  'gov.dse.database': 'قاعدة بيانات Athena',
  'gov.dse.tableName': 'جدول Athena',
  'gov.dse.loadColumns': 'تحميل الأعمدة من Athena',
  'gov.dse.loading': 'جارٍ التحميل…',
  'gov.dse.loaded': 'تم تحميل {n} أعمدة',
  'gov.dse.loadFailed': 'تعذّرت قراءة الجدول.',
  'gov.dse.columns': 'الأعمدة',
  'gov.dse.columnsHint': 'لا يُكشف إلا عن هذه الأعمدة. حدّد الحساس منها: الأعمدة المقيّدة مخفية ما لم تُظهرها سياسة.',
  'gov.dse.colName': 'العمود',
  'gov.dse.colLabel': 'التسمية',
  'gov.dse.colType': 'النوع',
  'gov.dse.colSensitivity': 'الحساسية',
  'gov.dse.addColumn': '+ إضافة عمود',
  'gov.dse.removeColumn': 'إزالة العمود {name}',
  'gov.dse.noColumns': 'لا توجد أعمدة بعد. حمّلها من Athena أو أضفها يدوياً.',
  'gov.dse.agents': 'الوكلاء',
  'gov.dse.agentsAll': 'كل الوكلاء',
  'gov.dse.agentsSome': 'وكلاء محددون فقط',
  'gov.dse.create': 'إنشاء مجموعة البيانات',
  'gov.dse.save': 'حفظ مجموعة البيانات',

  'gov.attr.heading': 'سمات المستخدمين',
  'gov.attr.desc': 'معلومات عن الأشخاص يمكن للسياسات استخدامها، مثل مركز التكلفة أو الرقم الوظيفي أو الدرجة. في القاعدة، قارن عموداً مع "سمة المستخدم".',
  'gov.attr.builtins': 'متاحة دائماً من الملف الشخصي: {list}.',
  'gov.attr.import': 'استيراد CSV',
  'gov.attr.template': 'تنزيل القالب',
  'gov.attr.importHint': 'العمود الأول: البريد الإلكتروني. ثم عمود لكل سمة. الخلايا الفارغة تُبقي القيم كما هي.',
  'gov.attr.importing': 'جارٍ الاستيراد…',
  'gov.attr.importDone': 'تم تحديث {users} مستخدمين وتعيين {values} قيم.',
  'gov.attr.importUnknown': 'لم يُعثر على {n} بريد إلكتروني: {list}',
  'gov.attr.importFailed': 'فشل الاستيراد.',
  'gov.attr.importTitle': 'تم استيراد السمات',
  'gov.attr.search': 'البحث عن مستخدمين',
  'gov.attr.colUser': 'المستخدم',
  'gov.attr.colDept': 'القسم',
  'gov.attr.colAttrs': 'السمات',
  'gov.attr.none': 'لا يوجد',
  'gov.attr.edit': 'تعديل',
  'gov.attr.editFor': 'تعديل سمات {name}',
  'gov.attr.editTitle': 'السمات: {name}',
  'gov.attr.key': 'السمة',
  'gov.attr.value': 'القيمة',
  'gov.attr.add': '+ إضافة سمة',
  'gov.attr.remove': 'إزالة {key}',
  'gov.attr.keyHint': 'أسماء بأحرف إنجليزية صغيرة، مثل cost_center. افصل القيم المتعددة بفواصل (مثل مدير لديه مركزا تكلفة).',
  'gov.attr.saved': 'تم حفظ السمات',
  'gov.attr.saveFailed': 'تعذّر حفظ السمات.',
  'gov.attr.empty': 'لا يوجد مستخدمون مطابقون.',
  'gov.attr.invalidKey': '"{key}" ليس اسم سمة صالحاً.',

  'gov.test.heading': 'اختبار كمستخدم',
  'gov.test.desc': 'شاهد بالضبط ما سيحصل عليه الشخص، محسوباً بالمحرك نفسه الذي يطبّق القواعد.',
  'gov.test.user': 'المستخدم',
  'gov.test.chooseUser': 'اختر مستخدماً',
  'gov.test.agent': 'الوكيل',
  'gov.test.anyAgent': 'كل مجموعات البيانات (أي وكيل)',
  'gov.test.check': 'فحص الوصول',
  'gov.test.checking': 'جارٍ الفحص…',
  'gov.test.allowed': 'لديه وصول',
  'gov.test.denied': 'لا وصول',
  'gov.test.rowScope': 'الصفوف: {scope}',
  'gov.test.columns': 'الأعمدة',
  'gov.test.policies': 'السياسات',
  'gov.test.policyApplied': 'مطبّقة',
  'gov.test.policyNotApplies': 'لا تنطبق على هذا المستخدم',
  'gov.test.policySkipped': 'تنطبق، لكن تم تخطيها: {reason}',
  'gov.test.sql': 'العرض المُولَّد (SQL)',
  'gov.test.attributes': 'السمات: {list}',
  'gov.test.noAttributes': 'ليس لهذا المستخدم أي سمات.',
  'gov.test.noDatasets': 'لا توجد مجموعات بيانات محكومة لهذا الوكيل.',
  'gov.test.query': 'جرّب استعلاماً',
  'gov.test.queryHint': 'يُنفَّذ على Athena بصفة هذا المستخدم عبر حارس السياسات، ويُسجَّل باسمك. يعرض حتى 50 صفاً.',
  'gov.test.run': 'تشغيل الاستعلام',
  'gov.test.running': 'جارٍ التشغيل…',
  'gov.test.rows': '{n} صفوف',
  'gov.test.truncated': 'توجد صفوف أخرى؛ تُعرض أول {n} فقط.',
  'gov.test.refused': 'مرفوض: {error}',
  'gov.test.failed': 'تعذّر إجراء الفحص.',

  'gov.log.heading': 'النشاط',
  'gov.log.desc': 'كل استعلام محكوم وكل رفض وحجب. لا تُخزَّن البيانات المُعادة ولا القيم المسرّبة أبداً.',
  'gov.log.filter': 'عرض',
  'gov.log.all': 'الكل',
  'gov.log.action.query': 'استعلام',
  'gov.log.action.denied': 'مرفوض',
  'gov.log.action.redacted': 'محجوب',
  'gov.log.action.withheld': 'محتجز',
  'gov.log.refresh': 'تحديث',
  'gov.log.colTime': 'الوقت',
  'gov.log.colUser': 'المستخدم',
  'gov.log.colAgent': 'الوكيل',
  'gov.log.colAction': 'الحدث',
  'gov.log.colDatasets': 'مجموعات البيانات',
  'gov.log.colRows': 'الصفوف',
  'gov.log.colDetail': 'التفاصيل',
  'gov.log.empty': 'لم يُسجَّل شيء بعد.',
  'gov.log.loadFailed': 'تعذّر تحميل سجل النشاط.',
};

const governance = { en, ar };
export default governance;
